/**
 * @summary
 *  숫자를 1K, 1M, 1B 등의 형태로 변환하는 포맷터,
 *  10000 이상 부터 K, M, B 단위로 변환
 */
export const humanReadableNumberFormatter = {
  UNITS: {
    K: 1_000,
    M: 1_000_000,
    B: 1_000_000_000,
  },

  /**
   * @summary
   *   숫자를 단위 문자열로 변환
   * @example
   * formatNumber.to(1000) // 1,000
   * formatNumber.to(10000) // 10K
   * formatNumber.to(10000000) // 10M
   */
  to(value: number): string {
    if (!value) {
      return '0';
    }

    if (value < 0) {
      return `-${this.to(-value)}`;
    }

    const { K, M, B } = this.UNITS;

    if (value < K * 10) {
      return value.toLocaleString('ko-KR');
    }

    if (value < M) {
      return `${Math.floor(value / K)}K`;
    }

    if (value < B) {
      return `${Math.floor(value / M)}M`;
    }

    return `${Math.floor(value / B)}B`;
  },

  /**
   * @summary 단위 문자열을 숫자로 변환
   * - K 이상의 단위는 to 메서드에서 이하 자리수를 버리므로, from 메서드에서는 정확한 값을 구할 수 없음
   * @example
   * formatNumber.from('1,000') // 1000
   * formatNumber.from('10K') // 10000
   * formatNumber.from('10M') // 10000000
   */
  from(str: string): number {
    if (!str) {
      return 0;
    }

    if (!this.isFormatted(str)) {
      return parseFloat(str.replace(/,/g, ''));
    }

    const { K, M, B } = this.UNITS;
    const unit = str.slice(-1).toUpperCase(); // 마지막 문자 확인 (K, M, B 등)
    const value = parseFloat(str.slice(0, -1).trim()); // 숫자 부분 파싱

    switch (unit) {
      case 'K':
        return value * K;
      case 'M':
        return value * M;
      case 'B':
        return value * B;
      default:
        return parseFloat(str.replace(/,/g, '')); // 단위가 없으면 쉼표 제거 후 숫자로 변환
    }
  },

  /**
   * K, M, B 단위로 포맷팅된 문자열인지 확인
   */
  isFormatted(str: string): boolean {
    if (!str || typeof str !== 'string') {
      return false;
    }

    const regex = /^\d+(\.\d+)?[KMB]$/i; // 숫자 + 선택적 소수점 + 단위(K, M, B)
    return regex.test(str.replace(/,/g, '')); // 쉼표 제거 후 정규식 검사
  },
};

const makeValueWithFloorUnit = (num: number, unit: number) => {
  if (num % unit == 0) {
    return (num / unit).toLocaleString('ko-KR');
  }
  return (Math.floor((num * 10) / unit) / 10).toLocaleString('ko-KR');
};

export const humanReadableNumberV2 = {
  UNITS: {
    K: 1_000,
    M: 1_000_000,
    B: 1_000_000_000,
  },
  UNITS_KR: {
    man: 10_000,
    uck: 100_000_000,
  },
  /**
   * @summary
   *   숫자를 단위 문자열로 변환
   * @example
   * formatNumber.to(1000, false) // 1,000
   * formatNumber.to(11100, false) // 11.1K
   * formatNumber.to(1110000, false) // 11.1M
   * formatNumber.to(1000, true) // 1,000
   * formatNumber.to(11100, true) // 1.1만
   * formatNumber.to(1110000, true) // 111만
   * formatNumber.to(111000000, true) // 1.1억
   */
  to(value: number, is_kr: boolean): { value: string; unit: string } {
    if (!value) {
      return { value: '0', unit: '' };
    }

    if (!is_kr) {
      if (value < 0) {
        return { value: `-${this.to(-value, is_kr)}`, unit: '' };
      }

      const { K, M, B } = this.UNITS;

      if (value < K * 10) {
        return { value: value.toLocaleString('ko-KR'), unit: '' };
      }

      if (value < M) {
        return { value: makeValueWithFloorUnit(value, K), unit: 'K' };
      }

      if (value < B) {
        return { value: makeValueWithFloorUnit(value, M), unit: 'M' };
      }

      return { value: makeValueWithFloorUnit(value, B), unit: 'B' };
    } else {
      if (value < 0) {
        return { value: `-${this.to(-value, is_kr)}`, unit: '' };
      }

      const { man, uck } = this.UNITS_KR;

      if (value < man) {
        return { value: value.toLocaleString('ko-KR'), unit: '' };
      }

      if (value < man * 10) {
        return { value: makeValueWithFloorUnit(value, man), unit: '만' };
      }
      if (value < uck) {
        return { value: Math.floor(value / man).toLocaleString('ko-KR'), unit: '만' };
      }
      return { value: makeValueWithFloorUnit(value, uck), unit: '억' };
    }
  },
};
